/**
 * Datasets 화면의 표시 문구.
 *
 * 오류 코드 → 문구 매핑을 이 파일 **한 곳**이 소유한다(규칙 §11). 서버 원문(`detail`)을 그대로
 * 쓰지 않는다 — 영어 기술 문장이고 사용자가 다음에 무엇을 할지 말해 주지 않는다(규칙 §5).
 *
 * Owns:
 * - 종류 · 포맷 · 노출 · 빈 목록 · 오류의 사용자 문구, 긴 digest 의 축약 표기.
 *
 * Does not own:
 * - 시각 · 크기 포맷 — `@/shared/utils`. Probe 문구 — `probeStatus.ts` · `probeFailureMessage.ts`.
 */
import { isHttpClientError } from "@/shared/http";
import { formatDateTime } from "@/shared/utils/datetime/format";
import type { DatasetFormat, DatasetSourceKind } from "../domain/dataset";
import type { DatasetFilters } from "./datasetFilters";

const SOURCE_KIND_LABEL: Record<DatasetSourceKind, string> = {
  azure_prefix: "Azure prefix",
  hf_repo: "Hugging Face",
};

export function formatSourceKind(kind: DatasetSourceKind): string {
  return SOURCE_KIND_LABEL[kind];
}

const FORMAT_LABEL: Record<DatasetFormat, string> = {
  lerobot: "LeRobot",
  v1: "Hatchery V1",
  rlds: "RLDS",
  tfds: "TFDS",
  misc: "Misc",
};

/** 포맷 칩의 글자. 버전이 있으면 함께 보여 어느 세대인지 한눈에 안다. */
export function formatDatasetFormat(format: DatasetFormat, version: string | null): string {
  return version === null ? FORMAT_LABEL[format] : `${FORMAT_LABEL[format]} ${version}`;
}

/** 서버가 판정 근거를 주지 않을 때 쓰는 사유. */
const PROBE_LOCK_FALLBACK = "이 원본은 지금 엔진으로 샘플을 뜰 수 없습니다.";

/**
 * Probe 할 수 없는 원본의 사유.
 *
 * 판정 근거(`formatDetail`)는 서버가 사용자에게 보일 한국어 문장이다. 오류 envelope 의 영어
 * `detail` 과 다르다. 이 함수는 "왜 안 되는지" 뒤에 "무엇을 볼 수 있는지" 를 붙인다. 그러면 사용자가
 * 버튼을 다시 누르지 않는다(규칙 §5).
 */
export function formatProbeLockReason(formatDetail: string | null): string {
  return `${formatDetail ?? PROBE_LOCK_FALLBACK} 파일 목록은 볼 수 있습니다.`;
}

export function formatVisibility(hiddenAtMs: number | null): string {
  return hiddenAtMs === null ? "목록에 표시" : `숨김 (${formatDateTime(hiddenAtMs)})`;
}

/** 앞 12자만 보이고 전체는 복사 버튼이 갖는다 — 64자 digest 가 줄을 다 먹지 않게. */
export function shortDigest(value: string): string {
  return value.length <= 12 ? value : `${value.slice(0, 12)}…`;
}

/**
 * 빈 목록의 문구.
 *
 * "등록된 것이 없다" 와 "조건에 맞는 것이 없다" 는 사용자가 할 일이 다르다 — 한 문장으로 합치면
 * 검색어를 지우면 될 상황에서 새 Dataset 을 등록하게 만든다.
 */
export function formatEmptyMessage(filters: DatasetFilters): string {
  if (filters.search !== "") {
    return filters.includeHidden
      ? "검색 조건에 맞는 Dataset 이 없습니다. 검색어를 지우고 다시 찾아 주세요."
      : "검색 조건에 맞는 Dataset 이 없습니다. 검색어를 지우거나 숨긴 항목을 포함해 보세요.";
  }
  if (filters.includeHidden) {
    return "아직 등록된 Dataset 이 없습니다. `Dataset 등록` 으로 원본 주소를 추가해 주세요.";
  }
  return "보이는 Dataset 이 없습니다. 숨긴 항목을 포함하거나 `Dataset 등록` 으로 원본 주소를 추가해 주세요.";
}

const MESSAGE_BY_CODE: Record<string, string> = {
  "dataset.not_found": "요청한 Dataset 을 찾을 수 없습니다.",
  "probe.not_found": "요청한 Probe 를 찾을 수 없습니다.",
  "candidate.not_found": "요청한 후보를 찾을 수 없습니다. 목록을 새로고침해 주세요.",
  "candidate.already_decided": "이미 처리된 후보입니다. 목록을 새로고침하면 지금 상태가 보입니다.",
  "probe.format_unsupported": formatProbeLockReason(null),
  "dataset.source_invalid": "원본 주소를 등록할 수 없습니다. 주소를 다시 확인해 주세요.",
  "request.validation_failed": "요청 값이 올바르지 않습니다. 입력을 다시 확인해 주세요.",
  "auth.jwks.unavailable":
    "인증 서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요. 로그인은 유지됩니다.",
};

/** 실패를 사용자 문구로 옮긴다. 서버 원문 · 스택은 노출하지 않는다. */
export function resolveDatasetErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    return "요청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }

  const byCode = error.code === null ? undefined : MESSAGE_BY_CODE[error.code];
  if (byCode !== undefined) {
    return byCode;
  }

  switch (error.kind) {
    case "network":
      return "서버에 연결하지 못했습니다. 네트워크 상태를 확인해 주세요.";
    case "timeout":
      return "요청 시간이 초과되었습니다. 잠시 후 다시 시도해 주세요.";
    case "server":
      return "서버에서 요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
    case "forbidden":
      return "이 작업을 수행할 권한이 없습니다.";
    default:
      return "요청을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }
}
